import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  ArrowUpRight,
  CalendarDays,
  Clapperboard,
  GraduationCap,
  Images,
  KanbanSquare,
  Lightbulb,
  MessageCircle,
  Sparkles,
  Truck,
  Users,
  Wallet,
} from "lucide-react";
import {
  api,
  type BartThread,
  type CarouselItem,
  type Comment,
  type Discussion,
  type FulfillmentSummary,
  type SalesDailyPoint,
  type Subsystem,
  type Task,
  type WeekSlot,
} from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn, formatNumber, formatPLN } from "@/lib/utils";
import { OpCallout } from "@/components/op/OpCallout";
import { OpNowBar } from "@/components/op/OpNowBar";
import { OP_SURFACE } from "@/lib/opSurfaces";

type Priority = {
  id: string;
  text: string;
  href: string;
  tone: "critical" | "warn" | "info";
};

const CAROUSEL_ACTION_STATUSES = new Set([
  "idea",
  "draft",
  "approved",
  "scheduled",
  "publishing",
  "failed",
]);

const CAROUSEL_STATUS_PL: Record<string, string> = {
  idea: "pomysły",
  draft: "szkic",
  approved: "zatwierdzona",
  scheduled: "zaplanowana",
  publishing: "publikacja",
  failed: "błąd",
};

function todayLabel(): string {
  const raw = new Date().toLocaleDateString("pl-PL", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

function todayWeekday(): number {
  return (new Date().getDay() + 6) % 7;
}

function startOfToday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function isOverdue(deadline: string | null | undefined): boolean {
  if (!deadline) return false;
  const d = new Date(deadline);
  if (Number.isNaN(d.getTime())) return false;
  return d < startOfToday();
}

function isDueToday(deadline: string | null | undefined): boolean {
  if (!deadline) return false;
  const d = new Date(deadline);
  if (Number.isNaN(d.getTime())) return false;
  return d.toDateString() === new Date().toDateString();
}

function kindShort(kind?: string): string {
  switch (kind) {
    case "carousel":
      return "karuzela";
    case "feed_post":
      return "post";
    case "reel_brief":
      return "rolka";
    case "story_brief":
      return "relacja";
    default:
      return kind || "slot";
  }
}

function statusCounts(items: Subsystem[] | undefined) {
  const list = items ?? [];
  return {
    ok: list.filter((s) => s.state === "ok").length,
    warn: list.filter((s) => s.state === "warn").length,
    down: list.filter((s) => s.state === "down").length,
    total: list.length,
  };
}

function buildPriorities(input: {
  fulfillment?: FulfillmentSummary;
  actionComments: number;
  overdueTasks: Task[];
  dueTodayTasks: Task[];
  todaySlots: WeekSlot[];
  carouselsNeeding: CarouselItem[];
  naradaRunning: boolean;
}): Priority[] {
  const out: Priority[] = [];
  const f = input.fulfillment;

  if (f && (f.complaints ?? 0) > 0) {
    out.push({
      id: "complaints",
      text: `${f.complaints} reklamacj${f.complaints === 1 ? "a" : "e"} w wysyłce`,
      href: "/wysylka",
      tone: "critical",
    });
  }
  if (f && (f.to_ship ?? 0) > 0) {
    out.push({
      id: "to_ship",
      text: `${f.to_ship} paczek do wysyłki (${f.ready_to_ship ?? 0} gotowych)`,
      href: "/wysylka",
      tone: "critical",
    });
  }
  if (f && (f.dedication ?? 0) > 0) {
    out.push({
      id: "dedication",
      text: `${f.dedication} z dedykacją do przygotowania`,
      href: "/wysylka",
      tone: "warn",
    });
  }
  if (input.overdueTasks.length > 0) {
    const first = input.overdueTasks[0];
    out.push({
      id: "overdue",
      text:
        input.overdueTasks.length === 1
          ? `Przeterminowane: ${first.title}`
          : `${input.overdueTasks.length} zadań po terminie (np. ${first.title})`,
      href: "/kanban",
      tone: "critical",
    });
  } else if (input.dueTodayTasks.length > 0) {
    out.push({
      id: "due_today",
      text: `${input.dueTodayTasks.length} zadań z terminem dziś`,
      href: "/kanban",
      tone: "warn",
    });
  }
  if (input.actionComments > 0) {
    out.push({
      id: "comments",
      text: `${input.actionComments} komentarzy do akcji`,
      href: "/komentarze",
      tone: "warn",
    });
  }
  const openSlots = input.todaySlots.filter((s) => !s.content_item_id && s.status !== "done");
  if (openSlots.length > 0) {
    out.push({
      id: "plan",
      text: `${openSlots.length} slotów planu na dziś bez materiału`,
      href: "/plan-tygodnia",
      tone: "info",
    });
  }
  if (input.carouselsNeeding.length > 0) {
    out.push({
      id: "carousels",
      text: `${input.carouselsNeeding.length} karuzel wymaga uwagi`,
      href: "/karuzele",
      tone: "info",
    });
  }
  if (input.naradaRunning) {
    out.push({
      id: "ai",
      text: "Zespół AI właśnie czyta",
      href: "/zespol-ai",
      tone: "info",
    });
  }

  const order = [
    "complaints",
    "to_ship",
    "overdue",
    "due_today",
    "comments",
    "dedication",
    "plan",
    "carousels",
    "ai",
  ];
  out.sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));
  return out.slice(0, 5);
}

function anyLoading(queries: { isLoading: boolean }[]): boolean {
  return queries.some((q) => q.isLoading);
}

export default function Dzis() {
  const navigate = useNavigate();
  const statusQ = useQuery({ queryKey: ["status"], queryFn: api.listStatus });
  const salesQ = useQuery({ queryKey: ["sales-summary"], queryFn: api.salesSummary });
  const dailyQ = useQuery({
    queryKey: ["sales-daily", 7],
    queryFn: () => api.salesDaily(7),
  });
  const fulfillmentQ = useQuery({
    queryKey: ["fulfillment-summary"],
    queryFn: api.fulfillmentSummary,
  });
  const commentsStatsQ = useQuery({
    queryKey: ["comments-stats", "24h"],
    queryFn: () => api.commentsStats({ window: "24h" }),
  });
  const commentsActionQ = useQuery({
    queryKey: ["comments-action", "24h"],
    queryFn: () =>
      api.listComments({ window: "24h", action: true, limit: 5, offset: 0 }),
  });
  const tasksQ = useQuery({ queryKey: ["tasks"], queryFn: () => api.listTasks() });
  const weekQ = useQuery({ queryKey: ["week-plan"], queryFn: () => api.weekPlan() });
  const carouselsQ = useQuery({
    queryKey: ["carousels-list"],
    queryFn: () => api.carouselList(),
  });
  const discussionsQ = useQuery({
    queryKey: ["ai-team-discussions"],
    queryFn: api.listDiscussions,
    refetchInterval: (q) =>
      (q.state.data || []).some((d) => d.status === "running") ? 5000 : false,
  });
  const bartQ = useQuery({
    queryKey: ["bart-threads"],
    queryFn: api.bartThreads,
  });

  const counts = statusCounts(statusQ.data);
  const actionCount =
    commentsStatsQ.data?.payload?.action ?? commentsActionQ.data?.total ?? 0;
  const tasks = tasksQ.data;

  const todo = useMemo(
    () => (tasks ?? []).filter((t) => t.bucket === "todo"),
    [tasks],
  );
  const inProgress = useMemo(
    () => (tasks ?? []).filter((t) => t.bucket === "in_progress"),
    [tasks],
  );
  const openTasks = useMemo(() => [...todo, ...inProgress], [todo, inProgress]);
  const overdueTasks = useMemo(
    () => openTasks.filter((t) => isOverdue(t.deadline)),
    [openTasks],
  );
  const dueTodayTasks = useMemo(
    () => openTasks.filter((t) => isDueToday(t.deadline)),
    [openTasks],
  );

  const todaySlots = useMemo(() => {
    const wd = todayWeekday();
    return (weekQ.data?.slots ?? []).filter((s) => s.weekday === wd);
  }, [weekQ.data]);

  const carouselsNeeding = useMemo(() => {
    return (carouselsQ.data?.items ?? [])
      .filter((c) => CAROUSEL_ACTION_STATUSES.has(c.status))
      .slice(0, 6);
  }, [carouselsQ.data]);

  const discussions = discussionsQ.data ?? [];
  const naradaRunning = discussions.some((d) => d.status === "running");
  const lastDiscussion = discussions[0];
  const bartRecent = (bartQ.data?.items ?? []).slice(0, 3);

  const priorities = useMemo(
    () =>
      buildPriorities({
        fulfillment: fulfillmentQ.data,
        actionComments: actionCount,
        overdueTasks,
        dueTodayTasks,
        todaySlots,
        carouselsNeeding,
        naradaRunning,
      }),
    [
      fulfillmentQ.data,
      actionCount,
      overdueTasks,
      dueTodayTasks,
      todaySlots,
      carouselsNeeding,
      naradaRunning,
    ],
  );

  const spark = (dailyQ.data?.series ?? []).map((p: SalesDailyPoint) => p.revenue);
  const f = fulfillmentQ.data;

  return (
    <div className="-mx-1 rounded-2xl border border-border bg-[#f7f8fa] p-3 text-foreground shadow-sm sm:-mx-0 sm:p-6">
      <PageHeader
        title="Dziś"
        subtitle={todayLabel()}
        actions={
          <StatusBadges
            loading={statusQ.isLoading}
            error={statusQ.isError}
            counts={counts}
          />
        }
      />

      <section className="mb-5">
        <SectionLabel>Na dziś</SectionLabel>
        {priorities.length === 0 &&
        !anyLoading([
          fulfillmentQ,
          commentsStatsQ,
          tasksQ,
          weekQ,
          carouselsQ,
          discussionsQ,
        ]) ? (
          <OpCallout variant="help" title="Spokojny dzień" className="border-slate-200">
            Brak pilnych rzeczy. Możesz zajrzeć do Podsumowania albo Planu tygodnia.
          </OpCallout>
        ) : (
          <div className="space-y-2">
            {priorities[0] ? (
              <OpNowBar
                sticky={false}
                label={priorities[0].text}
                detail="Pierwszy priorytet dnia"
                actionLabel="Otwórz"
                onAction={() => {
                  navigate(priorities[0].href);
                }}
              />
            ) : null}
            {priorities.slice(1).map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: (i + 1) * 0.04 }}
              >
                <Link
                  to={p.href}
                  className={cn(
                    "flex items-start gap-3 px-3.5 py-3 text-sm transition-colors",
                    p.tone === "critical" && OP_SURFACE.danger.box,
                    p.tone === "warn" && OP_SURFACE.review.box,
                    p.tone === "info" && OP_SURFACE.help.box,
                  )}
                >
                  <AlertTriangle
                    className={cn(
                      "mt-0.5 h-4 w-4 shrink-0",
                      p.tone === "critical" && "text-red-600",
                      p.tone === "warn" && "text-orange-600",
                      p.tone === "info" && "text-yellow-700",
                    )}
                  />
                  <span className="flex-1 font-medium text-slate-800">{p.text}</span>
                  <ArrowUpRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
                </Link>
              </motion.div>
            ))}
            {anyLoading([fulfillmentQ, commentsStatsQ, tasksQ, weekQ, carouselsQ]) &&
              priorities.length === 0 && (
                <p className="text-sm text-muted-foreground">Ładowanie priorytetów…</p>
              )}
          </div>
        )}
      </section>

      <section className="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <KpiTile
          title="Sprzedaż dziś"
          value={salesQ.isError ? "n/d" : formatPLN(salesQ.data?.dzis_pln ?? 0)}
          sub={
            salesQ.isLoading
              ? "Ładowanie…"
              : `${formatPLN(salesQ.data?.dni7_pln ?? 0)} / 7 dni`
          }
          icon={Wallet}
          href="/sprzedaz"
          spark={spark}
          loading={salesQ.isLoading}
        />
        <KpiTile
          title="Do wysyłki"
          value={fulfillmentQ.isError ? "n/d" : String(f?.to_ship ?? 0)}
          sub={
            fulfillmentQ.isLoading
              ? "Ładowanie…"
              : `nadane dziś: ${f?.shipped_today ?? 0}`
          }
          icon={Truck}
          href="/wysylka"
          loading={fulfillmentQ.isLoading}
          accent={f && f.to_ship > 0 ? "warn" : undefined}
        />
        <KpiTile
          title="Komentarze do akcji"
          value={commentsStatsQ.isError ? "n/d" : String(actionCount)}
          sub={
            commentsStatsQ.isLoading
              ? "Ładowanie…"
              : `${commentsStatsQ.data?.payload?.total ?? 0} w oknie 24h`
          }
          icon={MessageCircle}
          href="/komentarze"
          loading={commentsStatsQ.isLoading}
          accent={actionCount > 0 ? "warn" : undefined}
        />
        <KpiTile
          title="Zadania otwarte"
          value={tasksQ.isError ? "n/d" : String(openTasks.length)}
          sub={
            tasksQ.isLoading
              ? "Ładowanie…"
              : `${todo.length} todo / ${inProgress.length} w toku`
          }
          icon={KanbanSquare}
          href="/kanban"
          loading={tasksQ.isLoading}
        />
      </section>

      <div className="grid gap-5 lg:grid-cols-2">
        <div className="space-y-5">
          <QueueCard
            title="Wysyłka"
            href="/wysylka"
            loading={fulfillmentQ.isLoading}
            error={fulfillmentQ.isError}
          >
            {f ? (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                <MiniStat label="Do wysyłki" value={f.to_ship} />
                <MiniStat label="Gotowe" value={f.ready_to_ship} />
                <MiniStat label="Dedykacje" value={f.dedication} />
                <MiniStat label="Nadane dziś" value={f.shipped_today} />
                <MiniStat label="Nowe" value={f.new} />
                <MiniStat label="Reklamacje" value={f.complaints} warn={f.complaints > 0} />
              </div>
            ) : (
              <EmptyLine>Brak danych wysyłki</EmptyLine>
            )}
          </QueueCard>

          <QueueCard
            title="Komentarze do akcji"
            href="/komentarze"
            loading={commentsActionQ.isLoading}
            error={commentsActionQ.isError}
          >
            {(commentsActionQ.data?.items ?? []).length === 0 ? (
              <EmptyLine>Brak komentarzy do akcji w 24h</EmptyLine>
            ) : (
              <ul className="space-y-2">
                {(commentsActionQ.data?.items ?? []).map((c: Comment) => (
                  <li
                    key={c.id}
                    className="rounded-lg border border-slate-100 bg-slate-50/80 px-3 py-2"
                  >
                    <div className="mb-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
                      <span className="font-semibold uppercase tracking-wide text-slate-600">
                        {c.platform}
                      </span>
                      <span className="truncate">{c.author}</span>
                    </div>
                    <p className="line-clamp-2 text-sm text-slate-800">{c.text}</p>
                  </li>
                ))}
              </ul>
            )}
          </QueueCard>

          <QueueCard
            title="Kanban"
            href="/kanban"
            loading={tasksQ.isLoading}
            error={tasksQ.isError}
          >
            {openTasks.length === 0 ? (
              <EmptyLine>Brak otwartych zadań</EmptyLine>
            ) : (
              <ul className="space-y-2">
                {[...overdueTasks, ...dueTodayTasks, ...openTasks]
                  .filter((t, i, arr) => arr.findIndex((x) => x.id === t.id) === i)
                  .slice(0, 5)
                  .map((t) => (
                    <li
                      key={t.id}
                      className={cn(
                        "rounded-lg border px-3 py-2 text-sm",
                        isOverdue(t.deadline)
                          ? "border-rose-200 bg-rose-50/60"
                          : "border-slate-100 bg-slate-50/80",
                      )}
                    >
                      <div className="font-medium text-slate-800">{t.title}</div>
                      <div className="mt-0.5 flex flex-wrap gap-2 text-[11px] text-muted-foreground">
                        <span>{t.bucket === "in_progress" ? "w toku" : "todo"}</span>
                        {t.owner && <span>{t.owner}</span>}
                        {isOverdue(t.deadline) && (
                          <span className="font-medium text-rose-600">po terminie</span>
                        )}
                        {isDueToday(t.deadline) && !isOverdue(t.deadline) && (
                          <span className="font-medium text-amber-600">dziś</span>
                        )}
                      </div>
                    </li>
                  ))}
              </ul>
            )}
          </QueueCard>
        </div>

        <div className="space-y-5">
          <QueueCard
            title="Plan na dziś"
            href="/plan-tygodnia"
            loading={weekQ.isLoading}
            error={weekQ.isError}
            icon={CalendarDays}
          >
            {todaySlots.length === 0 ? (
              <EmptyLine>Brak slotów na dziś w planie tygodnia</EmptyLine>
            ) : (
              <ul className="space-y-2">
                {todaySlots.map((s, i) => (
                  <li
                    key={s.id || `${s.weekday}-${s.kind}-${i}`}
                    className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 bg-slate-50/80 px-3 py-2 text-sm"
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-slate-800">
                        {kindShort(s.kind)}
                        {s.theme ? ` · ${s.theme}` : ""}
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        {(s.funnel_stage || "").toUpperCase()}
                        {s.owner ? ` · ${s.owner}` : ""}
                      </div>
                    </div>
                    <Badge variant={s.content_item_id ? "secondary" : "outline"}>
                      {s.content_item_id ? "jest" : "brak"}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </QueueCard>

          <QueueCard title="Tydzień Arka" href="/tydzien-arka" icon={CalendarDays}>
            <EmptyLine>Checklista operatora: nauka, sprzedaż, ads, ops</EmptyLine>
          </QueueCard>

          <QueueCard
            title="Kreator publikacji"
            href="/karuzele"
            loading={carouselsQ.isLoading}
            error={carouselsQ.isError}
            icon={Images}
          >
            {carouselsNeeding.length === 0 ? (
              <EmptyLine>Brak karuzel wymagających uwagi</EmptyLine>
            ) : (
              <ul className="space-y-2">
                {carouselsNeeding.map((c) => (
                  <li key={c.id}>
                    <Link
                      to="/karuzele"
                      className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 bg-slate-50/80 px-3 py-2 text-sm transition-colors hover:border-slate-300"
                    >
                      <span className="truncate font-medium text-slate-800">
                        {c.title || c.hook || c.short_id}
                      </span>
                      <Badge variant="outline">
                        {CAROUSEL_STATUS_PL[c.status] || c.status}
                      </Badge>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </QueueCard>

          <QueueCard
            title="Zespół AI"
            href="/zespol-ai"
            loading={discussionsQ.isLoading}
            error={discussionsQ.isError}
            icon={Users}
          >
            <AiBlock running={naradaRunning} last={lastDiscussion} />
          </QueueCard>

          <QueueCard
            title="Bart"
            href="/bart"
            loading={bartQ.isLoading}
            error={bartQ.isError}
            icon={GraduationCap}
          >
            {bartRecent.length === 0 ? (
              <EmptyLine>Brak wątków</EmptyLine>
            ) : (
              <ul className="space-y-2">
                {bartRecent.map((t: BartThread) => (
                  <li key={t.id}>
                    <Link
                      to="/bart"
                      className="block truncate rounded-lg border border-slate-100 bg-slate-50/80 px-3 py-2 text-sm font-medium text-slate-800 hover:border-slate-300"
                    >
                      {t.title || t.id}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </QueueCard>
        </div>
      </div>

      <footer className="mt-6 flex flex-wrap gap-2 border-t border-slate-200 pt-4">
        <Button asChild size="sm" variant="outline" className="bg-white">
          <Link to="/podsumowanie">
            <Lightbulb className="mr-1.5 h-3.5 w-3.5" />
            Podsumowanie
          </Link>
        </Button>
        <Button asChild size="sm" variant="outline" className="bg-white">
          <Link to="/sprzedaz">
            <Clapperboard className="mr-1.5 h-3.5 w-3.5" />
            Rolki vs sprzedaż
          </Link>
        </Button>
        <Button asChild size="sm" variant="ghost">
          <Link to="/status">Status systemów</Link>
        </Button>
      </footer>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
      <Sparkles className="h-3.5 w-3.5 text-brand" />
      {children}
    </div>
  );
}

function StatusBadges({
  loading,
  error,
  counts,
}: {
  loading: boolean;
  error: boolean;
  counts: { ok: number; warn: number; down: number; total: number };
}) {
  if (loading) {
    return <Badge variant="secondary">Status…</Badge>;
  }
  if (error) {
    return (
      <Button asChild size="sm" variant="outline" className="bg-white">
        <Link to="/status">Status niedostępny</Link>
      </Button>
    );
  }
  return (
    <>
      <Button asChild size="sm" variant="outline" className="gap-1.5 bg-white text-xs">
        <Link to="/status">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          {counts.ok}/{counts.total || "?"} OK
        </Link>
      </Button>
      {counts.warn > 0 && (
        <Badge variant="warning" className="text-xs">
          {counts.warn} ostrzeż.
        </Badge>
      )}
      {counts.down > 0 && (
        <Badge variant="destructive" className="text-xs">
          {counts.down} down
        </Badge>
      )}
    </>
  );
}

function KpiTile({
  title,
  value,
  sub,
  icon: Icon,
  href,
  spark,
  loading,
  accent,
}: {
  title: string;
  value: string;
  sub?: string;
  icon: React.ComponentType<{ className?: string }>;
  href: string;
  spark?: number[];
  loading?: boolean;
  accent?: "warn";
}) {
  return (
    <Link to={href} className="block">
      <Card
        className={cn(
          "h-full border-slate-200 bg-white shadow-none transition-colors hover:border-slate-300",
          accent === "warn" && "border-amber-200",
        )}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-slate-600">{title}</CardTitle>
          <Icon className="h-4 w-4 text-slate-400" />
        </CardHeader>
        <CardContent>
          <div
            className={cn(
              "text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl",
              loading && "animate-pulse text-slate-300",
            )}
          >
            {loading ? "…" : value}
          </div>
          {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
          {spark && spark.length > 0 && (
            <div className="mt-3 flex h-7 items-end gap-0.5">
              {spark.map((v, i) => {
                const max = Math.max(...spark, 1);
                const h = Math.max(8, (v / max) * 100);
                return (
                  <div
                    key={i}
                    className="flex-1 rounded-sm bg-brand/35"
                    style={{ height: `${h}%` }}
                  />
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}

function QueueCard({
  title,
  href,
  children,
  loading,
  error,
  icon: Icon,
}: {
  title: string;
  href: string;
  children: React.ReactNode;
  loading?: boolean;
  error?: boolean;
  icon?: React.ComponentType<{ className?: string }>;
}) {
  return (
    <Card className="border-slate-200 bg-white shadow-none">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4 text-slate-400" />}
          <CardTitle className="text-base">{title}</CardTitle>
        </div>
        <Button asChild size="sm" variant="ghost" className="h-8 text-xs">
          <Link to={href}>
            Otwórz <ArrowUpRight className="ml-1 h-3.5 w-3.5" />
          </Link>
        </Button>
      </CardHeader>
      <CardContent>
        {loading && <p className="text-sm text-muted-foreground">Ładowanie…</p>}
        {error && !loading && (
          <p className="text-sm text-muted-foreground">Nie udało się wczytać.</p>
        )}
        {!loading && !error && children}
      </CardContent>
    </Card>
  );
}

function MiniStat({
  label,
  value,
  warn,
}: {
  label: string;
  value: number;
  warn?: boolean;
}) {
  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50/80 px-2.5 py-2">
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div
        className={cn(
          "mt-0.5 text-lg font-semibold tabular-nums",
          warn ? "text-rose-600" : "text-slate-900",
        )}
      >
        {formatNumber(value)}
      </div>
    </div>
  );
}

function EmptyLine({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-muted-foreground">{children}</p>;
}

function AiBlock({
  running,
  last,
}: {
  running: boolean;
  last?: Discussion;
}) {
  return (
    <div className="space-y-2">
      <div
        className={cn(
          "rounded-lg border px-3 py-2.5 text-sm",
          running ? "border-brand/30 bg-brand/5" : "border-slate-100 bg-slate-50/80",
        )}
      >
        {running ? (
          <span className="font-medium text-slate-800">Zespół właśnie czyta.</span>
        ) : (
          <span className="text-slate-700">Zapytaj zespół. On wraca ze zdaniem.</span>
        )}
      </div>
      {last && (
        <p className="truncate text-xs text-muted-foreground">
          Ostatnia: {last.question || last.schema_label || `#${last.id}`}
          {last.status ? ` · ${last.status}` : ""}
        </p>
      )}
    </div>
  );
}
